import { supabase, TABLES } from './supabase';

export interface Notification {
  id: string;
  type: 'low_stock' | 'stock_movement' | 'new_part' | 'new_supplier' | 'system';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  data?: any;
}

export interface NotificationStats {
  unreadCount: number;
  totalCount: number;
}

export class NotificationService {
  private static notifications: Notification[] = [];
  private static listeners: ((notifications: Notification[]) => void)[] = [];
  private static isSubscribed = false;

  // Initialize real-time notifications
  static initialize() {
    if (this.isSubscribed) {
      return;
    }

    // Subscribe to parts table changes
    supabase
      .channel('notifications-parts-changes')
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: TABLES.VEHICLE_PARTS },
        (payload) => {
          this.addNotification({
            id: `new-part-${payload.new.id}`,
            type: 'new_part',
            title: 'New Part Added',
            message: `New part "${payload.new.name}" has been added to inventory`,
            timestamp: new Date().toISOString(),
            read: false,
            data: payload.new
          });
        }
      )
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: TABLES.VEHICLE_PARTS },
        (payload) => {
          // Check if stock went below minimum
          const oldStock = payload.old.current_stock;
          const newStock = payload.new.current_stock;
          const minStock = payload.new.minimum_stock;

          if (oldStock > minStock && newStock <= minStock) {
            this.addNotification({
              id: `low-stock-${payload.new.id}-${Date.now()}`,
              type: 'low_stock',
              title: 'Low Stock Alert',
              message: `"${payload.new.name}" is now low on stock (${newStock}/${minStock})`,
              timestamp: new Date().toISOString(),
              read: false,
              data: payload.new
            });
          }
        }
      )
      .subscribe();

    // Subscribe to stock movements
    supabase
      .channel('notifications-movements-changes')
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: TABLES.STOCK_MOVEMENTS },
        async (payload) => {
          // Get part details
          const { data: part } = await supabase
            .from(TABLES.VEHICLE_PARTS)
            .select('name')
            .eq('id', payload.new.part_id)
            .single();

          const partName = part?.name || 'Unknown Part';
          const movementType = payload.new.movement_type;
          const quantity = payload.new.quantity;

          let title = 'Stock Movement';
          let message = '';

          switch (movementType) {
            case 'in':
              title = 'Stock In';
              message = `+${quantity} units of "${partName}" added to inventory`;
              break;
            case 'out':
              title = 'Stock Out';
              message = `${quantity} units of "${partName}" removed from inventory`;
              break;
            case 'transfer':
              title = 'Stock Transfer';
              message = `${quantity} units of "${partName}" transferred`;
              break;
            case 'adjustment':
              title = 'Stock Adjustment';
              message = `Stock of "${partName}" adjusted to ${payload.new.new_stock} units`;
              break;
          }

          this.addNotification({
            id: `movement-${payload.new.id}`,
            type: 'stock_movement',
            title,
            message,
            timestamp: new Date().toISOString(),
            read: false,
            data: payload.new
          });
        }
      )
      .subscribe();

    // Subscribe to suppliers
    supabase
      .channel('notifications-suppliers-changes')
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: TABLES.SUPPLIERS },
        (payload) => {
          this.addNotification({
            id: `new-supplier-${payload.new.id}`,
            type: 'new_supplier',
            title: 'New Supplier Added',
            message: `New supplier "${payload.new.name}" has been added`,
            timestamp: new Date().toISOString(),
            read: false,
            data: payload.new
          });
        }
      )
      .subscribe();

    this.isSubscribed = true;
  }

  // Add a new notification
  private static addNotification(notification: Notification) {
    this.notifications.unshift(notification);
    
    // Keep only last 50 notifications
    if (this.notifications.length > 50) {
      this.notifications = this.notifications.slice(0, 50);
    }

    // Notify all listeners
    this.listeners.forEach(listener => listener([...this.notifications]));

    // Store in localStorage for persistence
    this.saveToStorage();
  }

  // Get all notifications
  static getNotifications(): Notification[] {
    return [...this.notifications];
  }

  // Get notification stats
  static getNotificationStats(): NotificationStats {
    const unreadCount = this.notifications.filter(n => !n.read).length;
    return {
      unreadCount,
      totalCount: this.notifications.length
    };
  }

  // Mark notification as read
  static markAsRead(notificationId: string) {
    const notification = this.notifications.find(n => n.id === notificationId);
    if (notification) {
      notification.read = true;
      this.listeners.forEach(listener => listener([...this.notifications]));
      this.saveToStorage();
    }
  }

  // Mark all notifications as read
  static markAllAsRead() {
    this.notifications.forEach(n => n.read = true);
    this.listeners.forEach(listener => listener([...this.notifications]));
    this.saveToStorage();
  }

  // Clear all notifications
  static clearAll() {
    this.notifications = [];
    this.listeners.forEach(listener => listener([]));
    this.saveToStorage();
  }

  // Subscribe to notification changes
  static subscribe(listener: (notifications: Notification[]) => void) {
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  // Load notifications from localStorage
  static loadFromStorage() {
    try {
      const stored = localStorage.getItem('vtms_notifications');
      if (stored) {
        this.notifications = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading notifications from storage:', error);
      this.notifications = [];
    }
  }

  // Save notifications to localStorage
  private static saveToStorage() {
    try {
      localStorage.setItem('vtms_notifications', JSON.stringify(this.notifications));
    } catch (error) {
      console.error('Error saving notifications to storage:', error);
    }
  }

  // Format timestamp for display
  static formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  }

  // Get notification icon based on type
  static getNotificationIcon(type: string) {
    switch (type) {
      case 'low_stock':
        return '⚠️';
      case 'stock_movement':
        return '📦';
      case 'new_part':
        return '➕';
      case 'new_supplier':
        return '🏢';
      default:
        return '🔔';
    }
  }

  // Get notification color based on type
  static getNotificationColor(type: string) {
    switch (type) {
      case 'low_stock':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'stock_movement':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'new_part':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'new_supplier':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  }
}
